/**
 * FullscreenManager — framework primitive for fullscreen toggle.
 *
 * Wraps Phaser's ScaleManager fullscreen API. Stored in game.registry
 * so any scene can access it. Provides a one-click toggle button
 * that scenes can add to their HUD.
 *
 * Browser requirement: fullscreen must be triggered from a pointerup
 * user-input gesture (not pointerdown).
 */

declare const Phaser: any;

import { Depth } from "./Depth";

export class FullscreenManager {
  private game: any;

  constructor(game: any) {
    this.game = game;
  }

  /** Whether the game is currently fullscreen. */
  get isFullscreen(): boolean {
    return this.game.scale.isFullscreen;
  }

  /** Toggle fullscreen on/off. Must be called from a pointerup gesture. */
  toggle(): void {
    this.game.scale.toggleFullscreen();
  }

  /**
   * Create a small fullscreen toggle button in a scene.
   * Returns the button objects so the caller can position/destroy them.
   */
  createButton(scene: any, x: number = 780, y: number = 20): { bg: any; icon: any } {
    const size = 24;

    const bg = scene.add.rectangle(x, y, size, size, 0x1f2937, 0.6)
      .setDepth(Depth.SYSTEM)
      .setInteractive({ useHandCursor: true });
    bg.setStrokeStyle(1, 0x374151);

    const icon = scene.add.text(x, y, "[ ]", {
      fontFamily: "monospace",
      fontSize: "10px",
      color: "#9ca3af",
    }).setOrigin(0.5).setDepth(Depth.SYSTEM);

    bg.on("pointerover", () => {
      bg.setFillStyle(0x374151, 0.8);
      icon.setColor("#e5e7eb");
    });

    bg.on("pointerout", () => {
      bg.setFillStyle(0x1f2937, 0.6);
      icon.setColor("#9ca3af");
    });

    // Must be pointerup — browsers block fullscreen from pointerdown
    bg.on("pointerup", () => {
      this.toggle();
    });

    return { bg, icon };
  }
}
